import type {
  EnergyConversionResponse,
  GameLeaderboardResponse,
  GameProfileResponse
} from '@lunit/shared-types';
import { api } from '@core/api/client';

// Игра — только по customer-сессии (K-086), одинаково для обоих входов: общий клиент ядра добавляет сессию
// входа (мини-апп — Authorization: Bearer, PWA — cookie) и X-Venue-Slug, на 401 мини-апп входит заново и
// повторяет запрос, ошибку Go-конверта отдаёт сообщением (голый fetch с фолбэком на localhost:3001 шёл без
// сессии и показывал «[object Object]»). silent — ошибки показывают сами экраны игры.
// userId в запросах — только сверка с сессией на сервере (Telegram ID игре не нужен).
const quiet = { silent: true };

// Ключ идемпотентности обмена: повтор того же запроса (повторный вход на 401, сетевой сбой) не списывает
// монеты второй раз.
function idempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export const gameApi = {
  async getProfile(userId: string): Promise<GameProfileResponse> {
    return api.get<GameProfileResponse>(`/game/profile/${userId}`, quiet);
  },

  async click(userId: string): Promise<{ earned: string; newBalance: string; totalClicks: number }> {
    return api.post(`/game/click`, { userId }, quiet);
  },

  async buyBuilding(userId: string, buildingType: string, quantity = 1) {
    return api.post(`/game/building/buy`, { userId, buildingType, quantity }, quiet);
  },

  async buyUpgrade(userId: string, upgradeId: string) {
    return api.post(`/game/upgrade/buy`, { userId, upgradeId }, quiet);
  },

  async getAchievements(userId: string) {
    return api.get(`/game/achievements/${userId}`, quiet);
  },

  async claimAchievement(userId: string, achievementId: string) {
    return api.post(`/game/achievement/claim`, { userId, achievementId }, quiet);
  },

  async getLeaderboard(
    period: 'daily' | 'weekly' | 'alltime' = 'weekly',
    limit = 50,
    venueId?: string
  ): Promise<GameLeaderboardResponse> {
    const params = new URLSearchParams({
      period,
      limit: limit.toString()
    });

    // Лидерборд — заведения сессии; 'demo' — заглушка страницы, пока заведение не загружено (сервер ответил бы 403).
    if (venueId && venueId !== 'demo') {
      params.set('venueId', venueId);
    }

    return api.get<GameLeaderboardResponse>(`/game/leaderboard?${params.toString()}`, quiet);
  },

  async convertEnergy(userId: string, coinsToSpend: number): Promise<EnergyConversionResponse> {
    const data = await api.post<EnergyConversionResponse['data']>(
      `/game/energy/convert`,
      { userId, coinsToSpend, idempotencyKey: idempotencyKey() },
      quiet
    );
    return { success: true, data };
  }
};
